import type Dialog from '@naturehouse/design-system/components/molecules/dialog/Dialog';
import type { LoginResponseType } from '@naturehouse/design-system/components/organisms/login-form/LoginForm';
import type LoginForm from '@naturehouse/design-system/components/organisms/login-form/LoginForm';
import { LoginEvents } from '@naturehouse/design-system/components/organisms/login-form/LoginForm';
import type { TurnstileInstance } from '@marsidev/react-turnstile';
import type { RefObject } from 'react';
import { createRef } from 'react';
import { DialogEvents } from '@naturehouse/design-system/components/molecules/dialog/Dialog';
import GTM from '../../gtm/GTM';
import TrackLogin from './TrackLogin';
import LoginManager from './LoginManager';
import CSRFTokenCollection from './CSRFTokenCollection';
import UserStateManager from './UserStateManager';
import { allowBodyScroll, preventBodyScroll } from '../../ui/body';

export interface LoginDialogInterface {
    show(): void;

    close(): void;

    successCallback: (response: LoginResponseType) => Promise<void>;
}

export default class LoginDialog implements LoginDialogInterface {
    static #instance: LoginDialog | null = null;

    static getInstance(dialogId: string = 'login-dialog'): LoginDialog {
        if (this.#instance === null || this.#instance.dialogId !== dialogId) {
            this.#instance = new LoginDialog(dialogId);
        }

        return this.#instance;
    }

    static destroyInstance(): void {
        if (this.#instance === null) {
            return;
        }

        allowBodyScroll();
        this.#instance.#dialog?.removeEventListener(DialogEvents.OPENED, preventBodyScroll);
        this.#instance.#dialog?.removeEventListener(DialogEvents.CLOSED, allowBodyScroll);
        this.#instance.#form?.removeEventListener(
            LoginEvents.LOGIN_SUCCESS,
            this.#instance.#handleSuccess
        );
        this.#instance.#form?.removeEventListener(
            LoginEvents.LOGIN_FAILED,
            this.#instance.#handleFailed
        );

        // Restore prototype submitForm so re-opening the dialog doesn't stack overrides.
        if (this.#instance.#form) {
            delete (this.#instance.#form as unknown as Record<string, unknown>).submitForm;
        }

        this.#instance = null;
    }

    readonly #dialog: Dialog;

    readonly #form: LoginForm;

    readonly #captchaRef: RefObject<TurnstileInstance | null> = createRef<TurnstileInstance>();

    readonly #userStateManager: UserStateManager = UserStateManager.getInstance();

    #captchaInput: HTMLInputElement | null = null;

    #captchaVerified: boolean = false;

    #captchaPending: boolean = false;

    #socialInterceptorsInitialized: boolean = false;

    public constructor(private readonly dialogId: string) {
        this.#dialog = document.getElementById(this.dialogId) as Dialog;
        this.#form = this.#dialog?.querySelector('[is="nh-login-form"]') as LoginForm;

        if (this.#dialog === null || this.#form === null) {
            return;
        }

        this.#dialog.addEventListener(DialogEvents.OPENED, preventBodyScroll);
        this.#dialog.addEventListener(DialogEvents.CLOSED, allowBodyScroll);

        this.#form.addEventListener(LoginEvents.LOGIN_SUCCESS, this.#handleSuccess);
        this.#form.addEventListener(LoginEvents.LOGIN_FAILED, this.#handleFailed);

        const form = this.#form as unknown as { submitForm: (event?: Event) => Promise<boolean> };
        const originalSubmitForm = form.submitForm.bind(this.#form);
        form.submitForm = async (event?: Event): Promise<boolean> => {
            if (this.#captchaVerified) {
                return originalSubmitForm(event);
            }

            event?.preventDefault();

            if (this.#captchaPending) {
                return false;
            }
            this.#captchaPending = true;

            this.#form.addEventListener(
                'captcha:success',
                () => {
                    this.#captchaVerified = true;
                    this.#captchaPending = false;
                    (this.#form as unknown as HTMLFormElement).requestSubmit();
                },
                { once: true }
            );

            this.#captchaInput?.dispatchEvent(new CustomEvent('captcha:execute'));
            return false;
        };
    }

    public async show(): Promise<void> {
        this.#captchaVerified = false;
        this.#captchaPending = false;
        this.#dialog.showModal();
        this.#captchaInput = this.#form.querySelector<HTMLInputElement>(
            '[data-role="captcha-input"]'
        );
        this.#renderCaptchaComponent();
        this.#initSocialLoginInterceptors();

        const data = await CSRFTokenCollection.getInstance().retrieve({ name: 'authenticate' });

        if (data) {
            this.#form.token = data.token;
        }
    }

    public close(): void {
        this.#dialog.close();
        LoginDialog.destroyInstance();
    }

    public async successCallback(response: LoginResponseType): Promise<void> {
        const redirectURL = response.isLandlord
            ? this.#dialog.dataset.landlordRedirect
            : this.#dialog.dataset.userRedirect;

        if (!redirectURL) {
            try {
                await GTM.setMetaUserHash(response.personalInformation.email);
                GTM.setMetaUserId(response.uuid);
                GTM.setMetaLoginMethod('email');
                TrackLogin.trackLoginEvent();
            } catch {
                // continue regardless of errors
            }

            return;
        }

        window.location.href = redirectURL;
    }

    readonly #handleSuccess = async (e: Event): Promise<void> => {
        const event = e as CustomEvent;
        this.#userStateManager.updateState({
            isLoggedIn: true,
            isLandlord: event.detail.isLandlord ?? false
        });
        await this.successCallback(event.detail);

        const loginManager = LoginManager.getInstance();
        loginManager.isLoggedIn = true;

        this.#dialog.close();
    };

    readonly #handleFailed = (): void => {
        this.#captchaVerified = false;
        this.#captchaPending = false;
        this.#captchaRef.current?.reset();
        this.#userStateManager.updateState({
            isLoggedIn: false,
            isLandlord: false
        });
    };

    readonly #renderCaptchaComponent = (): void => {
        if (!this.#captchaInput) {
            return;
        }

        document.dispatchEvent(
            new CustomEvent('render-captcha', {
                detail: {
                    input: this.#captchaInput,
                    options: {},
                    ref: this.#captchaRef
                }
            })
        );
    };

    readonly #initSocialLoginInterceptors = (): void => {
        if (this.#socialInterceptorsInitialized) {
            return;
        }

        this.#socialInterceptorsInitialized = true;

        this.#form
            .querySelectorAll<HTMLAnchorElement>('[data-role="social-login"]')
            .forEach((anchor) => {
                const originalHref = anchor.href;

                anchor.addEventListener('click', (e: MouseEvent) => {
                    e.preventDefault();

                    this.#form.addEventListener(
                        'captcha:success',
                        (event: Event) => {
                            const { token } = (event as CustomEvent<{ token: string }>).detail;
                            const url = new URL(originalHref);
                            url.searchParams.set('token', token);
                            window.location.href = url.toString();
                        },
                        { once: true }
                    );

                    this.#captchaInput?.dispatchEvent(new CustomEvent('captcha:execute'));
                });
            });
    };
}
